<script setup>
import { reactive, computed, onMounted, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import validations, { getLabelTitleErrorMessage } from './validations';
import {
  getRandomColor,
  PRESET_LABEL_COLORS,
} from 'dashboard/helper/labelColor';
import { useLabelSourceByEntity } from 'dashboard/composables/useLabelSourceByEntity';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  entityKey: {
    type: String,
    default: 'conversation',
  },
  prefillTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const entityKey = computed(() => props.entityKey);
const { uiFlags, create } = useLabelSourceByEntity(entityKey);
const isConversationEntity = computed(() => entityKey.value === 'conversation');

const state = reactive({
  title: '',
  description: '',
  color: '#000',
});

const v$ = useVuelidate(validations, state);
const { title, description, color } = toRefs(state);

onMounted(() => {
  color.value = getRandomColor();
  title.value = props.prefillTitle.toLowerCase();
});

const labelTitleErrorMessage = computed(() =>
  t(getLabelTitleErrorMessage(v$.value))
);

const onClose = () => emit('close');

const addLabel = async () => {
  try {
    const attrs = {
      color: color.value,
      title: title.value.toLowerCase(),
      description: description.value,
    };

    await create(attrs);
    useAlert(t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(error?.message || t('LABEL_MGMT.ADD.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="isConversationEntity ? $t('LABEL_MGMT.ADD.DESC') : ''"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="label-description"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />

      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
        <div class="flex flex-wrap gap-1.5 mb-4">
          <button
            v-for="preset in PRESET_LABEL_COLORS"
            :key="preset"
            type="button"
            class="h-5 w-5 rounded-md border border-solid border-n-weak hover:scale-110 transition-transform"
            :style="{ backgroundColor: preset }"
            :class="{
              'ring-2 ring-offset-1 ring-n-blue-8': color === preset,
            }"
            @click="color = preset"
          />
        </div>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="$t('LABEL_MGMT.FORM.CREATE')"
          :disabled="v$.title.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  :deep(input) {
    @apply lowercase;
  }
}
</style>
